'use client';

import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';

interface BrandValueSlidersProps {
  popularity: number;
  csr: number;
  culture: number;
  loyalty: number;
  onPopularityChange: (value: number) => void;
  onCsrChange: (value: number) => void;
  onCultureChange: (value: number) => void;
  onLoyaltyChange: (value: number) => void;
  darkMode: boolean;
  labels: {
    popularity: string;
    csr: string;
    culture: string;
    loyalty: string;
  };
  showValue?: boolean; // Show /10 in label (default: false)
  showHelp?: boolean;
  helpTexts?: {
    popularity?: string;
    csr?: string;
    culture?: string;
    loyalty?: string;
  };
}

export default function BrandValueSliders({
  popularity,
  csr,
  culture,
  loyalty,
  onPopularityChange,
  onCsrChange,
  onCultureChange,
  onLoyaltyChange,
  darkMode,
  labels,
  showValue = false,
  showHelp = false,
  helpTexts,
}: BrandValueSlidersProps) {
  const sliderStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: darkMode ? '#f5f5f7' : '#1d1d1f',
    marginBottom: '0.5rem',
  };

  const helpTextStyle = {
    fontSize: '0.75rem',
    color: darkMode ? '#a1a1a6' : '#6e6e73',
    marginTop: '0.25rem',
    fontStyle: 'italic',
  };

  return (
    <>
      <style jsx>{`
        input[type='range'] {
          width: 100%;
          height: 8px;
          border-radius: 8px;
          appearance: none;
          cursor: pointer;
          background: ${darkMode ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
        }

        /* Action blue (#0071e3 = theme indigo-600); hardcoded hex required in
           styled-jsx, which bypasses the Tailwind @theme palette */
        input[type='range']::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #0071e3;
          cursor: pointer;
          transition: background 0.2s;
        }

        input[type='range']::-webkit-slider-thumb:hover {
          background: #1a8cff;
        }

        input[type='range']::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #0071e3;
          border: 0;
          cursor: pointer;
          transition: background 0.2s;
        }

        input[type='range']::-moz-range-thumb:hover {
          background: #1a8cff;
        }
      `}</style>

      {/* Popularity Slider */}
      {/* data-ai-field tags match the AI form bridge field names (harmless
          where no form is registered, e.g. the settings preview) */}
      <div style={sliderStyle} data-ai-field="brand_popularity">
        <label style={labelStyle}>
          {labels.popularity}
          {showValue && ` ${popularity}/10`}
          <Tooltip {...tooltipData.popularity} wide={true} />
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={popularity}
          onChange={(e) => onPopularityChange(Number(e.target.value))}
        />
        {showHelp && helpTexts?.popularity && (
          <div style={helpTextStyle}>{helpTexts.popularity}</div>
        )}
      </div>

      {/* CSR Slider */}
      <div style={sliderStyle} data-ai-field="brand_csr">
        <label style={labelStyle}>
          {labels.csr}
          {showValue && ` ${csr}/10`}
          <Tooltip {...tooltipData.csr} wide={true} />
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={csr}
          onChange={(e) => onCsrChange(Number(e.target.value))}
        />
        {showHelp && helpTexts?.csr && <div style={helpTextStyle}>{helpTexts.csr}</div>}
      </div>

      {/* Culture Slider */}
      <div style={sliderStyle} data-ai-field="brand_culture">
        <label style={labelStyle}>
          {labels.culture}
          {showValue && ` ${culture}/10`}
          <Tooltip {...tooltipData.culture} wide={true} />
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={culture}
          onChange={(e) => onCultureChange(Number(e.target.value))}
        />
        {showHelp && helpTexts?.culture && <div style={helpTextStyle}>{helpTexts.culture}</div>}
      </div>

      {/* Fans/Loyalty Slider */}
      <div style={{ marginBottom: '0' }} data-ai-field="brand_loyalty">
        <label style={labelStyle}>
          {labels.loyalty}
          {showValue && ` ${loyalty}/10`}
          <Tooltip {...tooltipData.fansLoyalty} wide={true} />
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={loyalty}
          onChange={(e) => onLoyaltyChange(Number(e.target.value))}
        />
        {showHelp && helpTexts?.loyalty && <div style={helpTextStyle}>{helpTexts.loyalty}</div>}
      </div>
    </>
  );
}
